import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Globe2, LockKeyhole } from "lucide-react";
import type { PublicTeam } from "@/lib/types";
import { teamAgeGroup, teamBadgeLabel } from "@/lib/teamBadge";
import {
  LiveStatusPill,
  type LiveStatusPillState,
} from "@/features/live/components/LiveStatusPill";

/**
 * `TeamCard` — shared public/member directory card for `/teams`.
 *
 * Brand kit consumer: every color/spacing routes through tokens
 * (`bg-surface`, `border-rule`, `text-ink`, etc.) so the card
 * inherits light + dark theme parity from `tokens.css`.
 *
 * The card surfaces the slim directory payload: name, slug, match count,
 * visibility when supplied, and the latest played match date. The optional
 * live pill comes only from the public aggregate poll; private cards never
 * infer "offline" from an aggregate that deliberately excludes them. The
 * whole card stays one Link/tap target.
 */
export function TeamCard({
  team,
  liveState,
  visibility,
  relationship,
}: {
  team: PublicTeam;
  relationship?: string;
  liveState?: LiveStatusPillState;
  visibility?: "public" | "private";
}) {
  const isMembership = Boolean(
    relationship &&
    relationship !== "Public viewing" &&
    relationship !== "Operational access",
  );
  const relationshipLabel =
    relationship === "Public viewing" ? "Public access" : relationship;
  const relationshipContext = isMembership
    ? "Your team"
    : relationship === "Operational access"
      ? "Club administration"
      : "Open to everyone";
  const matchSummary = `${team.match_count} ${team.match_count === 1 ? "match" : "matches"}`;
  // The team's OWN season (#908 D1), never the viewer's selected one; a
  // legacy team has none.
  const identityMeta = [
    teamAgeGroup(team.name),
    team.game_format,
    team.season?.name,
    team.status === "archived" ? "Archived" : null,
  ].filter(Boolean);

  return (
    <Link
      to="/teams/$teamSlug"
      params={{ teamSlug: team.slug }}
      data-testid="team-card"
      data-team-slug={team.slug}
      className={`td-card group ${isMembership ? "mine" : ""}`}
    >
      <div className="td-card-top">
        <div className="td-team-identity">
          <span className="td-team-mark" aria-hidden="true">
            {teamBadgeLabel(team.name)}
          </span>
          <div>
            <h3>{team.name}</h3>
            <p>{identityMeta.join(" · ")}</p>
          </div>
        </div>
        <div className="td-card-badges">
          {visibility ? (
            <span
              data-testid="team-card-visibility"
              className={`td-visibility ${visibility}`}
            >
              {visibility === "public" ? (
                <Globe2 aria-hidden="true" />
              ) : (
                <LockKeyhole aria-hidden="true" />
              )}
              {visibility === "public" ? "Public" : "Private"}
            </span>
          ) : null}
          {liveState ? <LiveStatusPill state={liveState} /> : null}
        </div>
      </div>
      {relationship ? (
        <div className="td-relationship" data-testid="team-card-relationship">
          <span>{relationshipLabel}</span>
          <small>{relationshipContext}</small>
        </div>
      ) : null}
      <dl className="td-card-meta">
        <div>
          <dt className="sr-only">Matches</dt>
          <dd data-testid="team-card-match-count">{matchSummary}</dd>
        </div>
        {team.last_match_at ? (
          <div>
            <dt>Last match</dt>
            <dd data-testid="team-card-last-at" className="text-ink">
              {team.last_match_at}
            </dd>
          </div>
        ) : null}
      </dl>
      <span className="td-card-footer" aria-hidden="true">
        <span>Results &amp; recordings</span>
        <span className="td-card-action">
          View team <ArrowUpRight />
        </span>
      </span>
    </Link>
  );
}
